import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Collapse,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { toast } from 'react-toastify';
import {
  DEFAULT_INVOICE_SETTINGS,
  INVOICE_PROVIDER_OPTIONS,
  isServerManagedProvider,
  branchLabel,
  resolveInvoiceIntegrationReadiness
} from '../../utils/invoiceSchema';
import { mergeInvoiceSettings, saveInvoiceSettings } from '../../utils/invoiceFirestoreService';
import {
  SART_ISLEM_TURU,
  SART_KATEGORI,
  sartUrunSecenekleri,
  SART_ODEME_KANALI,
  SART_ODEME_SEKLI,
  SART_TUTAR_KOSULU,
  SART_SONUCU,
  createInvoiceRule,
  mergeInvoiceRules,
  ruleIsShadowed
} from '../../utils/invoiceRules';
import { pingEdmConnection } from '../../utils/invoiceEdmApi';
import {
  DEFAULT_INVOICE_PRODUCT_CATALOG,
  buildCatalogRow,
  mergeInvoiceProductCatalog
} from '../../utils/invoiceProductCatalog';
import {
  DEFAULT_AMOUNT_NEAREST_PRODUCT_RULE,
  mergeAmountNearestProductRule
} from '../../utils/invoiceAmountProductRule';
import { mergeKrediKartiRule } from '../../utils/invoiceKrediKartiRule';
import InvoiceAmlSettingsSection from './InvoiceAmlSettingsSection';
import { InvoiceIdentitySettingsSection } from './InvoiceIdentityRiskPanel';

/**
 * Ayarlar dört başlıkta toplandı (eskiden yedi sekmeydi).
 * "Otomasyon" tek satır anahtar için ayrı bir boş sayfa kaplıyordu; Firma ile
 * aynı sekmeye alındı. Sağlayıcı + Entegratör ikisi de "nasıl gönderiyoruz",
 * MASAK + Kimlik ikisi de risk ayarı olduğu için birleştirildi.
 */
const SETTINGS_TAB = {
  GENERAL: 'genel',
  EFATURA: 'efatura',
  RULES: 'sartlar',
  RISK: 'risk'
};

const RULES_SUBTAB = {
  OTOMATIK: 'otomatik',
  SARRAFIYE: 'Sarrafiye',
  GRAM: 'Gram Altın',
  TAKI: 'Takı Ürün'
};

const settingsTabsSx = {
  minHeight: 40,
  flexShrink: 0,
  borderBottom: '1px solid #e2e8f0',
  '& .MuiTabs-indicator': { height: 2.5, borderRadius: 2, bgcolor: '#1d4ed8' },
  '& .MuiTab-root': {
    minHeight: 40,
    textTransform: 'none',
    fontWeight: 750,
    fontSize: 13.5,
    color: '#64748b',
    px: 1.75
  },
  '& .Mui-selected': { color: '#1d4ed8 !important' }
};

/** Okunabilir alanlar — kutuları şişirmeden */
const fieldSx = {
  '& .MuiInputBase-input': { fontSize: 14, py: 0.9, fontWeight: 600 },
  '& .MuiInputLabel-root': { fontSize: 13, fontWeight: 600 },
  '& .MuiInputBase-root': { minHeight: 40 },
  '& .MuiFormHelperText-root': { display: 'none' },
  '& input[type=number]': { MozAppearance: 'textfield' },
  '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
    WebkitAppearance: 'none',
    margin: 0
  }
};

/** Şart satırındaki kutular — yan yana sığsın diye yazı biraz daha küçük. */
const sartFieldSx = {
  ...fieldSx,
  '& .MuiInputBase-input': { fontSize: 12.5, py: 0.85, fontWeight: 600 },
  '& .MuiInputLabel-root': { fontSize: 11.5, fontWeight: 600 },
  // Coklu secimde metin uzuyor; kesmek yerine kutu icinde alt satira sariyor.
  '& .MuiSelect-select': {
    fontSize: 12.5,
    fontWeight: 600,
    whiteSpace: 'normal',
    lineHeight: 1.3,
    textOverflow: 'clip'
  },
  '& .MuiInputBase-root': { minHeight: 38, alignItems: 'flex-start' },
  '& .MuiFormHelperText-root': { display: 'none' }
};

const cardSx = {
  borderRadius: 2,
  border: '1px solid #e2e8f0',
  p: 1.5,
  bgcolor: '#fff',
  height: 'fit-content',
  alignSelf: 'stretch',
  minWidth: 0,
  maxWidth: '100%',
  width: '100%',
  boxSizing: 'border-box',
  overflow: 'hidden'
};

/**
 * Uzun kural aciklamalari icin katlanabilir kutu.
 *
 * Fatura Kurallari ekraninda ayarlarin kendisi aciklama metinlerinin arasinda
 * kayboluyordu; metinler artik varsayilan olarak KAPALI, isteyen acip okuyor.
 */
const Aciklama = ({ children, baslik = 'Nasıl çalışır?' }) => {
  const [acik, setAcik] = useState(false);
  return (
    <Box sx={{ mb: acik ? 1.25 : 0.75 }}>
      <Button
        size="small"
        onClick={() => setAcik((v) => !v)}
        sx={{
          textTransform: 'none',
          fontWeight: 700,
          fontSize: 12.5,
          color: '#64748b',
          p: 0,
          minWidth: 0,
          '&:hover': { background: 'transparent', color: '#334155' }
        }}
      >
        {acik ? '▾' : '▸'} {baslik}
      </Button>
      <Collapse in={acik} unmountOnExit>
        <Box sx={{ mt: 0.75 }}>{children}</Box>
      </Collapse>
    </Box>
  );
};

const SectionTitle = ({ children }) => (
  <Typography
    sx={{
      fontSize: 13,
      fontWeight: 800,
      color: '#475569',
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
      mb: 1.25
    }}
  >
    {children}
  </Typography>
);

const switchLabelSx = {
  ml: 0,
  mr: 1.25,
  my: 0,
  '& .MuiFormControlLabel-label': { fontSize: 13.5, fontWeight: 650, color: '#334155' }
};

const getActivePersonel = () => {
  try {
    const raw = localStorage.getItem('aktifPersonel');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const PasswordField = ({ label, value, onChange, disabled, show, onToggleShow }) => (
  <TextField
    label={label}
    size="small"
    fullWidth
    type={show ? 'text' : 'password'}
    value={value}
    onChange={onChange}
    disabled={disabled}
    sx={fieldSx}
    InputProps={{
      endAdornment: (
        <InputAdornment position="end">
          <IconButton size="small" onClick={onToggleShow} edge="end" disabled={disabled} aria-label="göster/gizle">
            {show ? <VisibilityOffIcon sx={{ fontSize: 18 }} /> : <VisibilityIcon sx={{ fontSize: 18 }} />}
          </IconButton>
        </InputAdornment>
      )
    }}
  />
);

function InvoiceSettingsPanel({ sube, settings, loading, canEdit }) {
  const [form, setForm] = useState({ ...DEFAULT_INVOICE_SETTINGS });
  const [saving, setSaving] = useState(false);
  const [pinging, setPinging] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [activeTab, setActiveTab] = useState(SETTINGS_TAB.GENERAL);
  const [rulesSubTab, setRulesSubTab] = useState(RULES_SUBTAB.OTOMATIK);

  useEffect(() => {
    setForm(mergeInvoiceSettings(settings));
  }, [settings]);

  const readiness = useMemo(() => resolveInvoiceIntegrationReadiness(form), [form]);
  const showIntegrator = Boolean(form.provider && form.provider !== 'none');

  const setField = (key) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  /**
   * Seri alanlari: 3 karaktere kirpilip buyuk harfe cevriliyor. e-Arsiv serisi
   * degisince eski `invoicePrefix` alani da guncelleniyor — sunucu, e-Arsiv
   * serisi bos kalirsa ona dusuyor (invoiceNumberAllocator.prefixForDocument).
   */
  const setSeries = (key) => (e) => {
    const value = String(e.target.value || '')
      .replace(/[^A-Za-z0-9]/g, '')
      .toUpperCase()
      .slice(0, 3);
    setForm((prev) => ({
      ...prev,
      [key]: value,
      ...(key === 'invoicePrefixEArsiv' ? { invoicePrefix: value } : {})
    }));
  };

  /**
   * Ust bloktaki kosul kurallarinin sayisi. Alttaki iki kural bolumu de
   * numaralandirilir ve bu sayiya gore kayar — kullanici 4. sart eklerse
   * asagidakiler 5 ve 6 olur, numaralar cakismaz.
   */
  const sartlar = mergeInvoiceRules(form.invoiceRules);
  // Urun kosulunun secenekleri katalogdan gelir; katalog degisince liste de degisir.
  const urunSecenekleri = React.useMemo(
    () => sartUrunSecenekleri(form.productCatalog),
    [form.productCatalog]
  );

  /** Şartı listede yukarı/aşağı taşır — sıra, hangi şartın önce deneneceğini belirler. */
  const tasi = (index, yon) =>
    setForm((prev) => {
      const liste = mergeInvoiceRules(prev.invoiceRules);
      const hedef = index + yon;
      if (hedef < 0 || hedef >= liste.length) return prev;
      [liste[index], liste[hedef]] = [liste[hedef], liste[index]];
      return { ...prev, invoiceRules: liste };
    });

  const showInvoiceSave = [SETTINGS_TAB.GENERAL, SETTINGS_TAB.EFATURA, SETTINGS_TAB.RULES].includes(
    activeTab
  );

  const handleSave = async () => {
    const personel = getActivePersonel();
    if (!personel?.id) {
      toast.error('Personel oturumu bulunamadı.');
      return;
    }
    setSaving(true);
    try {
      const result = await saveInvoiceSettings({
        sube,
        settings: form,
        updatedByPersonel: {
          id: personel.id,
          isim: personel.isim || '',
          soyisim: personel.soyisim || ''
        }
      });
      toast.success('Fatura ayarları kaydedildi.');
      const failed = result?.companySyncFailedBranches || [];
      if (failed.length) {
        toast.warning(
          `Firma bilgisi diğer şubelere yazılamadı (${failed.map(branchLabel).join(', ')}). ` +
            'Firma alanlarını tüm şubelerde eşitlemek için yönetici hesabı gerekir.'
        );
      }
    } catch (err) {
      toast.error(err?.message || 'Ayarlar kaydedilemedi.');
    } finally {
      setSaving(false);
    }
  };

  const handlePingEdm = async () => {
    setPinging(true);
    try {
      const result = await pingEdmConnection({ testMode: form.testMode !== false });
      if (result?.ok || result?.success) {
        toast.success(result.message || 'EDM bağlantısı başarılı');
      } else {
        toast.error(result?.message || 'EDM bağlantı testi başarısız');
      }
    } catch (err) {
      toast.error(err?.message || 'EDM bağlantı testi başarısız');
    } finally {
      setPinging(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ flex: 1, display: 'grid', placeItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  const readOnly = !canEdit;

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        minWidth: 0,
        width: '100%',
        maxWidth: '100%',
        height: '100%',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.25,
        boxSizing: 'border-box'
      }}
    >
      {/* Üst bar */}
      <Stack direction="row" alignItems="center" spacing={1.25} sx={{ flexShrink: 0 }}>
        <Alert
          severity={readiness.severity}
          sx={{
            flex: 1,
            minWidth: 0,
            py: 0.35,
            borderRadius: 2,
            '& .MuiAlert-message': { fontSize: 13.5, fontWeight: 700 }
          }}
        >
          {readiness.title}
          {!readiness.hasCompany ? ' · Firma bilgisi eksik' : ''}
        </Alert>
        {readOnly ? (
          <Typography sx={{ fontSize: 13, color: '#b45309', fontWeight: 700, whiteSpace: 'nowrap' }}>
            Salt okunur
          </Typography>
        ) : null}
        {!readOnly && isServerManagedProvider(form.provider) && showInvoiceSave ? (
          <Button
            variant="outlined"
            size="small"
            disabled={pinging || saving}
            onClick={handlePingEdm}
            sx={{ textTransform: 'none', fontWeight: 800, fontSize: 13.5, px: 1.5, minHeight: 38 }}
          >
            Bağlantı
          </Button>
        ) : null}
        {!readOnly && showInvoiceSave ? (
          <Button
            variant="contained"
            size="small"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
            onClick={handleSave}
            sx={{
              textTransform: 'none',
              fontWeight: 800,
              fontSize: 13.5,
              px: 2,
              minHeight: 38,
              bgcolor: '#1d4ed8',
              '&:hover': { bgcolor: '#1e40af' }
            }}
          >
            Kaydet
          </Button>
        ) : null}
      </Stack>

      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={settingsTabsSx}
      >
        <Tab value={SETTINGS_TAB.GENERAL} label="Firma & Otomasyon" />
        <Tab value={SETTINGS_TAB.EFATURA} label="e-Fatura Bağlantısı" />
        <Tab value={SETTINGS_TAB.RULES} label="Fatura Kuralları" />
        <Tab value={SETTINGS_TAB.RISK} label="Risk" />
      </Tabs>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowX: 'hidden',
          overflowY: 'auto',
          pb: 0.5
        }}
      >
        {activeTab === SETTINGS_TAB.GENERAL ? (
          <Paper
            variant="outlined"
            sx={{
              ...cardSx,
              width: '100%',
              py: 1.25,
              px: 1.75,
              borderColor: form.automationEnabled ? '#e2e8f0' : '#fecaca',
              bgcolor: form.automationEnabled ? '#fff' : '#fef2f2'
            }}
          >
            <SectionTitle>Otomasyon</SectionTitle>
            <Typography sx={{ fontSize: 12.5, color: '#64748b', fontWeight: 600, mb: 1.25 }}>
              Banka hareketi eşleştirme ve taslak oluşturma kuralları
            </Typography>
            <Stack direction="row" alignItems="center" spacing={0.5} useFlexGap flexWrap="wrap">
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(form.automationEnabled)}
                    onChange={setField('automationEnabled')}
                    disabled={readOnly}
                    color={form.automationEnabled ? 'success' : 'error'}
                  />
                }
                label={form.automationEnabled ? 'Açık' : 'Kapalı'}
                sx={switchLabelSx}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(form.automaticMatching)}
                    onChange={setField('automaticMatching')}
                    disabled={readOnly || !form.automationEnabled}
                  />
                }
                label="Eşleştir"
                sx={switchLabelSx}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(form.runMatchOnOpen)}
                    onChange={setField('runMatchOnOpen')}
                    disabled={readOnly || !form.automationEnabled || !form.automaticMatching}
                  />
                }
                label="Açılışta"
                sx={switchLabelSx}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(form.automaticDraft)}
                    onChange={setField('automaticDraft')}
                    disabled={readOnly || !form.automationEnabled}
                  />
                }
                label="Taslak"
                sx={switchLabelSx}
              />
              <FormControlLabel
                control={<Switch checked={Boolean(form.automaticApproval)} disabled />}
                label="Onay kapalı"
                sx={{
                  ...switchLabelSx,
                  '& .MuiFormControlLabel-label': { fontSize: 13.5, fontWeight: 650, color: '#94a3b8' }
                }}
              />
              <FormControlLabel
                control={<Switch checked={Boolean(form.automaticSend)} disabled />}
                label="GİB kapalı"
                sx={{
                  ...switchLabelSx,
                  '& .MuiFormControlLabel-label': { fontSize: 13.5, fontWeight: 650, color: '#94a3b8' }
                }}
              />
            </Stack>
          </Paper>
        ) : null}

        {activeTab === SETTINGS_TAB.EFATURA ? (
          <Paper variant="outlined" sx={{ ...cardSx, maxWidth: 720 }}>
            <SectionTitle>Sağlayıcı</SectionTitle>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 1.1, minWidth: 0 }}>
              <TextField
                select
                label="Sağlayıcı"
                size="small"
                fullWidth
                disabled={readOnly}
                value={form.provider}
                onChange={setField('provider')}
                sx={{ ...fieldSx, gridColumn: '1 / -1' }}
              >
                {INVOICE_PROVIDER_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
              {form.provider === 'other' ? (
                <TextField
                  label="Sağlayıcı Adı"
                  size="small"
                  fullWidth
                  disabled={readOnly}
                  value={form.providerName}
                  onChange={setField('providerName')}
                  sx={{ ...fieldSx, gridColumn: '1 / -1' }}
                />
              ) : null}
              <TextField
                select
                label="Matrah"
                size="small"
                fullWidth
                disabled={readOnly}
                value={form.defaultMatrahType}
                onChange={setField('defaultMatrahType')}
                sx={fieldSx}
              >
                <MenuItem value="Özel Matrah">Özel Matrah</MenuItem>
                <MenuItem value="Standart">Standart</MenuItem>
              </TextField>
              <TextField
                select
                label="Ödeme"
                size="small"
                fullWidth
                disabled={readOnly}
                value={form.defaultPaymentType}
                onChange={setField('defaultPaymentType')}
                sx={fieldSx}
              >
                <MenuItem value="Havale/EFT">Havale/EFT</MenuItem>
                <MenuItem value="Nakit">Nakit</MenuItem>
                <MenuItem value="Kredi Kartı">Kredi Kartı</MenuItem>
              </TextField>
              <TextField
                label="Para"
                size="small"
                fullWidth
                disabled={readOnly}
                value={form.defaultCurrency}
                onChange={setField('defaultCurrency')}
                sx={fieldSx}
              />
              {/* e-Arsiv ve e-Fatura AYRI seri kullaniyor (Kolaysoft: "E-fatura ve
                  e-arsiv icin ayri belge on ekleri girilmelidir"). Ekranda tek alan
                  vardi ve o alan yalnizca yedekti: degistirmek hicbir sey yapmiyordu.
                  Seri sube basina degil, sirket basina; uc sube de ayni mukellef
                  adina ayni seriden kesiyor. */}
              <TextField
                label="e-Arşiv Serisi"
                size="small"
                fullWidth
                disabled={readOnly}
                value={form.invoicePrefixEArsiv ?? form.invoicePrefix ?? ''}
                onChange={setSeries('invoicePrefixEArsiv')}
                inputProps={{ maxLength: 3, style: { textTransform: 'uppercase' } }}
                helperText="3 karakter · tüm şubelerde aynı"
                sx={fieldSx}
              />
              <TextField
                label="e-Fatura Serisi"
                size="small"
                fullWidth
                disabled={readOnly}
                value={form.invoicePrefixEFatura ?? ''}
                onChange={setSeries('invoicePrefixEFatura')}
                inputProps={{ maxLength: 3, style: { textTransform: 'uppercase' } }}
                helperText="e-Arşivden farklı olmalı"
                sx={fieldSx}
              />
              <Stack direction="row" spacing={1} alignItems="center">
                <FormControlLabel
                  control={
                    <Switch checked={Boolean(form.enabled)} onChange={setField('enabled')} disabled={readOnly} />
                  }
                  label="GİB"
                  sx={switchLabelSx}
                />
                <FormControlLabel
                  control={
                    <Switch checked={Boolean(form.testMode)} onChange={setField('testMode')} disabled={readOnly} />
                  }
                  label="Test"
                  sx={switchLabelSx}
                />
              </Stack>
            </Box>
          </Paper>
        ) : null}

        {activeTab === SETTINGS_TAB.EFATURA && showIntegrator ? (
          <Paper variant="outlined" sx={{ ...cardSx, maxWidth: 720 }}>
            <SectionTitle>Entegratör</SectionTitle>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.1 }}>
              {!isServerManagedProvider(form.provider) ? (
                <TextField
                  label="API Base URL"
                  size="small"
                  fullWidth
                  disabled={readOnly}
                  value={form.apiBaseUrl}
                  onChange={setField('apiBaseUrl')}
                  sx={{ ...fieldSx, gridColumn: '1 / -1' }}
                />
              ) : null}
              {/* Kolaysoft'ta kullanici adi da sifre gibi Secret Manager'da; sunucu
                  buradaki degeri hic okumuyor. Duzenlenebilir birakilinca EDM'den
                  kalma eski kullanici adi gorunup yaniltiyordu. */}
              {isServerManagedProvider(form.provider) ? (
                <TextField
                  label="Kullanıcı"
                  size="small"
                  fullWidth
                  disabled
                  value="Secret Manager"
                  sx={fieldSx}
                />
              ) : (
                <TextField
                  label="Kullanıcı"
                  size="small"
                  fullWidth
                  disabled={readOnly}
                  value={form.apiUsername}
                  onChange={setField('apiUsername')}
                  sx={fieldSx}
                />
              )}
              {isServerManagedProvider(form.provider) ? (
                <TextField
                  label="Şifre"
                  size="small"
                  fullWidth
                  disabled
                  value={form.apiPasswordSet ? 'Secret Manager' : 'Anlaşma sonrası'}
                  sx={fieldSx}
                />
              ) : (
                <PasswordField
                  label={form.apiPasswordSet ? 'Şifre (kayıtlı)' : 'API Şifre'}
                  value={form.apiPassword}
                  onChange={setField('apiPassword')}
                  disabled={readOnly}
                  show={showPassword}
                  onToggleShow={() => setShowPassword((v) => !v)}
                />
              )}
              {!isServerManagedProvider(form.provider) ? (
                <>
                  <TextField
                    label="API Key"
                    size="small"
                    fullWidth
                    disabled={readOnly}
                    value={form.apiKey}
                    onChange={setField('apiKey')}
                    sx={fieldSx}
                  />
                  <PasswordField
                    label={form.apiSecretSet ? 'Secret (kayıtlı)' : 'API Secret'}
                    value={form.apiSecret}
                    onChange={setField('apiSecret')}
                    disabled={readOnly}
                    show={showSecret}
                    onToggleShow={() => setShowSecret((v) => !v)}
                  />
                </>
              ) : null}
              {/* Firma/Sube kodu EDM'e ozgu alanlardi; Kolaysoft kullanmiyor,
                  sunucu da okumuyor. Yalniz baska saglayicilar icin duruyor. */}
              {!isServerManagedProvider(form.provider) ? (
                <>
                  <TextField
                    label="Firma Kodu"
                    size="small"
                    fullWidth
                    disabled={readOnly}
                    value={form.companyCode}
                    onChange={setField('companyCode')}
                    sx={fieldSx}
                  />
                  <TextField
                    label="Şube Kodu"
                    size="small"
                    fullWidth
                    disabled={readOnly}
                    value={form.branchCode}
                    onChange={setField('branchCode')}
                    sx={fieldSx}
                  />
                </>
              ) : null}
            </Box>
          </Paper>
        ) : null}

        {activeTab === SETTINGS_TAB.GENERAL ? (
          <Paper variant="outlined" sx={{ ...cardSx, maxWidth: 720 }}>
            <SectionTitle>Firma Bilgileri</SectionTitle>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 1.1, minWidth: 0 }}>
              <TextField
                label="Ünvan"
                size="small"
                fullWidth
                disabled={readOnly}
                value={form.companyTitle}
                onChange={setField('companyTitle')}
                sx={{ ...fieldSx, gridColumn: '1 / -1' }}
              />
              <TextField
                label="Vergi No / TCKN"
                size="small"
                fullWidth
                disabled={readOnly}
                value={form.taxNumber}
                onChange={setField('taxNumber')}
                sx={fieldSx}
              />
              <TextField
                label="Vergi Dairesi"
                size="small"
                fullWidth
                disabled={readOnly}
                value={form.taxOffice}
                onChange={setField('taxOffice')}
                sx={fieldSx}
              />
              <TextField
                label="İl"
                size="small"
                fullWidth
                disabled={readOnly}
                value={form.city}
                onChange={setField('city')}
                sx={fieldSx}
              />
              <TextField
                label="İlçe"
                size="small"
                fullWidth
                disabled={readOnly}
                value={form.district}
                onChange={setField('district')}
                sx={fieldSx}
              />
              <TextField
                label="Adres"
                size="small"
                fullWidth
                disabled={readOnly}
                value={form.address}
                onChange={setField('address')}
                sx={{ ...fieldSx, gridColumn: '1 / -1' }}
              />
              <TextField
                label="Telefon"
                size="small"
                fullWidth
                disabled={readOnly}
                value={form.phone}
                onChange={setField('phone')}
                sx={fieldSx}
              />
              <TextField
                label="E-posta"
                size="small"
                fullWidth
                disabled={readOnly}
                value={form.email}
                onChange={setField('email')}
                sx={fieldSx}
              />
            </Box>
          </Paper>
        ) : null}

        {activeTab === SETTINGS_TAB.RULES ? (
          <Stack spacing={1.1} sx={{ width: '100%', maxWidth: '100%', minHeight: 0, flex: 1 }}>
            <Tabs
              value={rulesSubTab}
              onChange={(_, v) => setRulesSubTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              sx={settingsTabsSx}
            >
              <Tab value={RULES_SUBTAB.OTOMATIK} label="Otomatik şartlar" />
              <Tab value={RULES_SUBTAB.SARRAFIYE} label="Sarrafiye" />
              <Tab value={RULES_SUBTAB.GRAM} label="Gram Altın" />
              <Tab value={RULES_SUBTAB.TAKI} label="Takı Ürün" />
            </Tabs>

            {rulesSubTab === RULES_SUBTAB.OTOMATIK ? (
              <Paper variant="outlined" sx={{ ...cardSx, width: '100%' }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.25 }}>
                  <SectionTitle>Şartlar</SectionTitle>
                  {!readOnly ? (
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() =>
                        setForm((prev) => ({
                          ...prev,
                          invoiceRules: [...mergeInvoiceRules(prev.invoiceRules), createInvoiceRule()]
                        }))
                      }
                      sx={{
                        textTransform: 'none',
                        fontWeight: 800,
                        bgcolor: '#1d4ed8',
                        '&:hover': { bgcolor: '#1e40af' },
                        whiteSpace: 'nowrap'
                      }}
                    >
                      Şart ekle
                    </Button>
                  ) : null}
                </Stack>

                <Stack spacing={1}>
                  {sartlar.map((rule, index) => {
                    const yamala = (patch) =>
                      setForm((prev) => {
                        const liste = mergeInvoiceRules(prev.invoiceRules);
                        liste[index] = createInvoiceRule({ ...liste[index], ...patch });
                        return { ...prev, invoiceRules: liste };
                      });
                    // Yalniz ustteki sart yuzunden HIC calisamayacaksa uyar.
                    const cakisanlar = sartlar
                      .map((o, i) => (i < index && ruleIsShadowed(rule, o) ? i + 1 : null))
                      .filter(Boolean);
                    const cokluSec = (label, key, secenekler, genislik) => (
                      <TextField
                        select
                        label={label}
                        size="small"
                        disabled={readOnly}
                        value={rule[key]}
                        onChange={(e) =>
                          yamala({
                            [key]: typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value
                          })
                        }
                        SelectProps={{
                          multiple: true,
                          displayEmpty: true,
                          renderValue: (secili) =>
                            !secili.length
                              ? 'Hepsi'
                              : secili
                                  .map((v) => secenekler.find((o) => o.value === v)?.label || v)
                                  .join(', ')
                        }}
                        InputLabelProps={{ shrink: true }}
                        sx={{ ...sartFieldSx, flex: `1 1 ${genislik}px`, minWidth: genislik }}
                      >
                        {secenekler.map((o) => (
                          <MenuItem key={o.value} value={o.value} sx={{ fontSize: 13, fontWeight: 600 }}>
                            <Checkbox size="small" checked={rule[key].includes(o.value)} sx={{ py: 0.25 }} />
                            {o.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    );
                    const tekSec = (label, key, secenekler, genislik) => (
                      <TextField
                        select
                        label={label}
                        size="small"
                        disabled={readOnly}
                        value={rule[key]}
                        onChange={(e) => yamala({ [key]: e.target.value })}
                        sx={{ ...sartFieldSx, flex: `1 1 ${genislik}px`, minWidth: genislik }}
                      >
                        {secenekler.map((o) => (
                          <MenuItem key={o.value} value={o.value}>
                            {o.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    );
                    const sayiAlani = (label, key, genislik) => (
                      <TextField
                        label={label}
                        type="number"
                        size="small"
                        disabled={readOnly}
                        value={rule[key]}
                        onChange={(e) => yamala({ [key]: e.target.value })}
                        sx={{ ...sartFieldSx, flex: `1 1 ${genislik}px`, minWidth: genislik }}
                      />
                    );
                    return (
                      <Paper
                        key={rule.id}
                        variant="outlined"
                        sx={{
                          p: 1.1,
                          borderRadius: 2,
                          bgcolor: rule.enabled ? '#fff' : '#f8fafc',
                          borderColor: cakisanlar.length ? '#fca5a5' : '#e2e8f0'
                        }}
                      >
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.85 }}>
                          <Box
                            sx={{
                              width: 24,
                              height: 24,
                              borderRadius: '50%',
                              bgcolor: '#1d4ed8',
                              color: '#fff',
                              fontSize: 12.5,
                              fontWeight: 800,
                              display: 'grid',
                              placeItems: 'center',
                              flexShrink: 0
                            }}
                          >
                            {index + 1}
                          </Box>
                          <Switch
                            size="small"
                            checked={rule.enabled}
                            disabled={readOnly}
                            onChange={(e) => yamala({ enabled: e.target.checked })}
                          />
                          {cakisanlar.length ? (
                            <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#b91c1c' }}>
                              {cakisanlar.join(', ')}. şart bunu tamamen kapsıyor — bu şart hiç çalışmaz
                            </Typography>
                          ) : null}
                          <Box sx={{ flex: 1 }} />
                          {!readOnly ? (
                            <>
                              {/* Sira onemli: yukaridan asagi ilk uyan sart uygulanir. */}
                              <IconButton
                                size="small"
                                disabled={index === 0}
                                title="Yukarı taşı"
                                onClick={() => tasi(index, -1)}
                              >
                                <ArrowUpwardIcon sx={{ fontSize: 17 }} />
                              </IconButton>
                              <IconButton
                                size="small"
                                disabled={index === sartlar.length - 1}
                                title="Aşağı taşı"
                                onClick={() => tasi(index, 1)}
                              >
                                <ArrowDownwardIcon sx={{ fontSize: 17 }} />
                              </IconButton>
                              <IconButton
                                size="small"
                                title="Sil"
                                onClick={() =>
                                  setForm((prev) => ({
                                    ...prev,
                                    invoiceRules: mergeInvoiceRules(prev.invoiceRules).filter((_, i) => i !== index)
                                  }))
                                }
                              >
                                <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </>
                          ) : null}
                        </Stack>

                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.75,
                            flexWrap: 'wrap'
                          }}
                        >
                          {cokluSec('İşlem', 'islemTurleri', SART_ISLEM_TURU, 95)}
                          {cokluSec('Kategori', 'kategoriler', SART_KATEGORI, 160)}
                          {/* Kategori yetmeyen durumlar icin tek tek urun secimi.
                              Bos birakilirsa o kategorideki tum urunler. */}
                          {cokluSec('Ürün', 'urunler', urunSecenekleri, 160)}
                          {cokluSec('Ödeme', 'odemeKanallari', SART_ODEME_KANALI, 155)}
                          {tekSec('Banka payı', 'odemeSekli', SART_ODEME_SEKLI, 135)}
                          {tekSec('Tutar', 'tutarKosulu', SART_TUTAR_KOSULU, 125)}
                          {rule.tutarKosulu === 'arasinda' ? (
                            <>
                              {sayiAlani('En az (TL)', 'tutarMin', 100)}
                              {sayiAlani('En çok (TL)', 'tutarMax', 100)}
                            </>
                          ) : rule.tutarKosulu !== 'yok' ? (
                            sayiAlani('Tutar (TL)', 'tutarMin', 90)
                          ) : null}
                          {tekSec('Sonuç', 'sonuc', SART_SONUCU, 225)}
                          {rule.sonuc === 'enYakinUrun' ? sayiAlani('Kabul edilen fark (TL)', 'toleransTl', 130) : null}
                        </Box>
                      </Paper>
                    );
                  })}

                  {!sartlar.length ? (
                    <Typography sx={{ fontSize: 13, color: '#94a3b8', fontWeight: 600, py: 1 }}>
                      Şart yok. “Şart ekle” ile ekleyin — yukarıdan aşağı ilk uyan şart uygulanır.
                    </Typography>
                  ) : null}
                </Stack>
              </Paper>
            ) : (
              <Paper variant="outlined" sx={{ ...cardSx, width: '100%', display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}>
                <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ mb: 1 }} spacing={1}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <SectionTitle>{rulesSubTab}</SectionTitle>
                    <Alert
                      severity="info"
                      sx={{
                        mt: 0.75,
                        py: 0.6,
                        px: 1.25,
                        borderRadius: 1.5,
                        bgcolor: '#eff6ff',
                        border: '1px solid #bfdbfe',
                        '& .MuiAlert-message': { width: '100%', py: 0.15 },
                        '& .MuiAlert-icon': { color: '#1d4ed8', py: 0.6 }
                      }}
                    >
                      <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: '#1e3a8a', mb: 0.35 }}>
                        Değer alanı ne işe yarar?
                      </Typography>
                      <Typography sx={{ fontSize: 12.2, fontWeight: 600, color: '#334155', lineHeight: 1.45 }}>
                        {rulesSubTab === RULES_SUBTAB.SARRAFIYE ? (
                          <>
                            Sayı yazılırsa (ör. <b>1605</b>) faturada 1. satır = has fiyatı × 1,605 × adet; kalan tutar
                            işçilik olur.
                            <br />
                            <b>Değer boşsa:</b> işlemdeki gram <b>tam sayıysa</b> (5, 7…) →{' '}
                            <b>24 Ayar Gram Altın</b>; <b>küsuratlıysa</b> (2,35…) → <b>22 Ayar Bilezik</b>.
                          </>
                        ) : rulesSubTab === RULES_SUBTAB.GRAM ? (
                          <>
                            Sayı yazılırsa (ör. <b>916</b> / <b>995</b>) milyemdir; 1. satır = gram × has × milyem.
                            <br />
                            <b>8 AYAR</b> yazılırsa ürün faturada <b>8 Ayar Takı</b> olur.
                            <br />
                            <b>Değer boşsa:</b> gram tam sayı → <b>24 Ayar Gram Altın</b>; küsurat →{' '}
                            <b>22 Ayar Bilezik</b>.
                          </>
                        ) : (
                          <>
                            Sayı yazılırsa (ör. <b>916</b>) milyemdir; 1. satır = gram × has × milyem; kalan işçilik.
                            <br />
                            <b>Değer boşsa:</b> gram tam sayı → <b>24 Ayar Gram Altın</b>; küsurat →{' '}
                            <b>22 Ayar Bilezik</b>.
                          </>
                        )}
                      </Typography>
                    </Alert>
                  </Box>
                  {!readOnly ? (
                    <Stack direction="row" spacing={0.75}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() =>
                          setForm((prev) => ({
                            ...prev,
                            productCatalog: DEFAULT_INVOICE_PRODUCT_CATALOG.map((r) => ({ ...r }))
                          }))
                        }
                        sx={{ textTransform: 'none', fontWeight: 700 }}
                      >
                        Varsayılanı yükle
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() =>
                          setForm((prev) => ({
                            ...prev,
                            productCatalog: [
                              ...mergeInvoiceProductCatalog(prev.productCatalog),
                              buildCatalogRow(rulesSubTab, 'Yeni ürün', '')
                            ]
                          }))
                        }
                        sx={{
                          textTransform: 'none',
                          fontWeight: 800,
                          bgcolor: '#1d4ed8',
                          '&:hover': { bgcolor: '#1e40af' }
                        }}
                      >
                        Satır ekle
                      </Button>
                    </Stack>
                  ) : null}
                </Stack>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                    gap: 0.85,
                    alignContent: 'start',
                    width: '100%'
                  }}
                >
                  {mergeInvoiceProductCatalog(form.productCatalog)
                    .map((row, globalIndex) => ({ row, globalIndex }))
                    .filter(({ row }) => row.category === rulesSubTab)
                    .map(({ row, globalIndex }) => (
                      <Stack
                        key={row.id || `${row.productName}-${globalIndex}`}
                        direction="row"
                        spacing={0.6}
                        alignItems="center"
                        sx={{
                          p: 0.65,
                          border: '1px solid #e2e8f0',
                          borderRadius: 1.5,
                          bgcolor: '#f8fafc',
                          minWidth: 0
                        }}
                      >
                        <TextField
                          size="small"
                          fullWidth
                          label="Ürün"
                          disabled={readOnly}
                          value={row.productName}
                          onChange={(e) => {
                            const productName = e.target.value;
                            setForm((prev) => {
                              const list = mergeInvoiceProductCatalog(prev.productCatalog);
                              list[globalIndex] = buildCatalogRow(
                                list[globalIndex].category,
                                productName,
                                list[globalIndex].valueRaw
                              );
                              return { ...prev, productCatalog: list };
                            });
                          }}
                          sx={{
                            ...fieldSx,
                            flex: 1,
                            minWidth: 0,
                            '& .MuiInputBase-root': { minHeight: 34 },
                            '& .MuiInputBase-input': { fontSize: 12.5, py: 0.55, fontWeight: 650 },
                            '& .MuiInputLabel-root': { fontSize: 11.5, fontWeight: 650 }
                          }}
                        />
                        <TextField
                          size="small"
                          label="Değer"
                          disabled={readOnly}
                          placeholder="1605"
                          value={row.valueRaw}
                          onChange={(e) => {
                            const valueRaw = e.target.value;
                            setForm((prev) => {
                              const list = mergeInvoiceProductCatalog(prev.productCatalog);
                              list[globalIndex] = buildCatalogRow(
                                list[globalIndex].category,
                                list[globalIndex].productName,
                                valueRaw
                              );
                              return { ...prev, productCatalog: list };
                            });
                          }}
                          sx={{
                            ...fieldSx,
                            width: 108,
                            flexShrink: 0,
                            '& .MuiInputBase-root': { minHeight: 34 },
                            '& .MuiInputBase-input': { fontSize: 12.5, py: 0.55, fontWeight: 700 },
                            '& .MuiInputLabel-root': { fontSize: 11.5, fontWeight: 650 }
                          }}
                        />
                        {!readOnly ? (
                          <IconButton
                            size="small"
                            aria-label="satır sil"
                            onClick={() =>
                              setForm((prev) => ({
                                ...prev,
                                productCatalog: mergeInvoiceProductCatalog(prev.productCatalog).filter(
                                  (_, i) => i !== globalIndex
                                )
                              }))
                            }
                            sx={{ flexShrink: 0 }}
                          >
                            <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        ) : null}
                      </Stack>
                    ))}
                </Box>
              </Paper>
            )}
          </Stack>
        ) : null}

        {activeTab === SETTINGS_TAB.RISK ? (
          <Box sx={{ maxWidth: 720 }}>
            <InvoiceAmlSettingsSection canEdit={canEdit} />
          </Box>
        ) : null}

        {activeTab === SETTINGS_TAB.RISK ? (
          <Box sx={{ maxWidth: 720 }}>
            <InvoiceIdentitySettingsSection canEdit={canEdit} sube={sube} />
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}

export default InvoiceSettingsPanel;
